import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATUS_LABELS, STATUS_STYLES, fmtRupiah } from "@/lib/constants";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  AreaChart,
  Area,
} from "recharts";
import { FileWarning, Clock, Wrench, CheckCircle2, Plus, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";

const PIE_COLORS = ["#0D9488", "#0284C7", "#22C55E", "#F59E0B", "#EF4444", "#64748B", "#134E4A"];

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  if (!stats) return <div className="text-sm text-slate-400">Memuat…</div>;

  const cards = [
    { key: "menunggu_persetujuan", label: "Menunggu Persetujuan", icon: Clock, color: "text-amber-600 bg-amber-50" },
    { key: "dalam_perbaikan", label: "Dalam Perbaikan", icon: Wrench, color: "text-cyan-700 bg-cyan-50" },
    { key: "selesai", label: "Selesai Diperbaiki", icon: CheckCircle2, color: "text-teal-700 bg-teal-50" },
  ];

  const monthData = stats.by_month.map((m) => ({
    name: m.month,
    value: m.value,
  }));

  const costData = (stats.cost_by_month || []).map((m) => ({
    name: m.month,
    value: m.value,
  }));

  const compactRp = (v) => {
    if (v >= 1e9) return `${(v / 1e9).toFixed(1)} M`;
    if (v >= 1e6) return `${(v / 1e6).toFixed(1)} jt`;
    if (v >= 1e3) return `${Math.round(v / 1e3)} rb`;
    return `${v}`;
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight text-slate-900 md:text-5xl">
            Dashboard
          </h1>
          <p className="mt-2 text-base text-slate-500">
            Selamat datang, {user?.name}. Ringkasan laporan kerusakan kendaraan dinas.
          </p>
        </div>
        {(user?.role === "pelapor" || user?.role === "admin") && (
          <Button
            data-testid="dashboard-new-report-btn"
            className="rounded-full bg-[var(--sipkd-primary)] px-5 text-white transition-colors duration-300 hover:bg-[var(--sipkd-primary-hover)]"
            onClick={() => navigate("/reports/new")}
          >
            <Plus className="mr-1 h-4 w-4" /> Lapor Kerusakan
          </Button>
        )}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5">
        <div className="rounded-2xl bg-gradient-to-br from-teal-600 to-sky-700 p-6 text-white shadow-lg shadow-teal-900/10 transition-transform duration-300 hover:-translate-y-1">
          <div className="flex items-center gap-2 text-teal-50">
            <FileWarning className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-wider">Total Laporan</span>
          </div>
          <p className="mt-4 font-num text-4xl font-extrabold" data-testid="stat-total">
            {stats.total}
          </p>
        </div>
        {cards.map((c) => (
          <div
            key={c.key}
            className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-transform duration-300 hover:-translate-y-1"
            data-testid={`stat-${c.key}`}
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                {c.label}
              </span>
              <span className={`flex h-9 w-9 items-center justify-center rounded-full ${c.color}`}>
                <c.icon className="h-4 w-4" />
              </span>
            </div>
            <p className="mt-4 font-num text-4xl font-extrabold text-slate-900">
              {stats.by_status[c.key] || 0}
            </p>
          </div>
        ))}
        <div
          className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-transform duration-300 hover:-translate-y-1"
          data-testid="stat-total-cost"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Total Biaya
            </span>
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-teal-50 text-teal-700">
              <Wallet className="h-4 w-4" />
            </span>
          </div>
          <p className="mt-4 font-num text-2xl font-extrabold text-slate-900">
            {fmtRupiah((stats.cost_by_month || []).reduce((s, m) => s + m.value, 0))}
          </p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-md border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2">
          <h3 className="font-display text-base font-medium text-slate-900">Laporan per Bulan</h3>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748B" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748B" }} />
                <Tooltip />
                <Bar dataKey="value" fill="#0D9488" radius={[4, 4, 0, 0]} maxBarSize={44} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-md border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="font-display text-base font-medium text-slate-900">Per Divisi</h3>
          <div className="mt-4 h-64">
            {stats.by_division.length === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-slate-400">
                Belum ada data
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={stats.by_division}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={45}
                    outerRadius={80}
                    paddingAngle={2}
                  >
                    {stats.by_division.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Cost trend */}
      <div className="rounded-md border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-base font-medium text-slate-900">
            Tren Biaya Perbaikan per Bulan
          </h3>
          <span className="font-num text-sm font-bold text-[var(--sipkd-primary)]" data-testid="cost-trend-total">
            {fmtRupiah(costData.reduce((s, d) => s + d.value, 0))}
          </span>
        </div>
        <div className="mt-4 h-64" data-testid="cost-trend-chart">
          {costData.length === 0 || costData.every((d) => d.value === 0) ? (
            <div className="flex h-full items-center justify-center text-sm text-slate-400">
              Belum ada biaya perbaikan tercatat
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={costData}>
                <defs>
                  <linearGradient id="costGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0D9488" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#0D9488" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748B" }} />
                <YAxis tickFormatter={compactRp} tick={{ fontSize: 11, fill: "#64748B" }} width={48} />
                <Tooltip formatter={(v) => [fmtRupiah(v), "Biaya"]} />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#0D9488"
                  strokeWidth={2}
                  fill="url(#costGrad)"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Workshop performance */}
      {(stats.by_workshop || []).length > 0 && (
        <div className="rounded-md border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="font-display text-base font-medium text-slate-900">Kinerja Bengkel</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            Jumlah laporan ditangani, yang selesai, rata-rata waktu perbaikan, dan total biaya.
          </p>
          <div className="thin-scroll mt-4 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-2.5 font-semibold">Bengkel</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Ditangani</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Selesai</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Rata-rata Waktu</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Total Biaya</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {stats.by_workshop.map((w, i) => (
                  <tr key={i} data-testid={`workshop-stat-${i}`} className="hover:bg-slate-50">
                    <td className="px-4 py-2.5">
                      <span className="font-medium text-slate-800">{w.name}</span>
                      {w.external && (
                        <span className="ml-2 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">
                          Bengkel Lain
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right font-num font-semibold">{w.handled}</td>
                    <td className="px-4 py-2.5 text-right font-num">{w.completed}</td>
                    <td className="px-4 py-2.5 text-right font-num">
                      {w.avg_days != null ? `${w.avg_days} hari` : "—"}
                    </td>
                    <td className="px-4 py-2.5 text-right font-num font-medium text-[var(--sipkd-primary)]">
                      {fmtRupiah(w.total_cost)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Status breakdown */}
      <div className="rounded-md border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="font-display text-base font-medium text-slate-900">Sebaran Status</h3>
        <div className="mt-4 flex flex-wrap gap-3">
          {Object.keys(STATUS_LABELS).map((k) => (
            <div
              key={k}
              className={`flex items-center gap-2 rounded-md border px-3 py-2 ${STATUS_STYLES[k]}`}
            >
              <span className="text-xs font-semibold">{STATUS_LABELS[k]}</span>
              <span className="font-num text-sm font-bold">{stats.by_status[k] || 0}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
